/* eslint-disable no-useless-escape */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Provider } from 'use-http';
import tw from 'tailwind.macro';
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { applyTheme } from '../themes/utils';
import { Global, css } from '@emotion/core';
import { useHttpOptions, jsonApiAxios } from '../services/api';
import config from '../config';
import translations from '../translations/main.json';
import { dataLayer, gtm, hotjar, hotjarIdMap } from '../utils/externalScripts';
import { useCoBrandingParams } from '../hooks/useCobrandingParams';
import { isWithinInterval } from 'date-fns';

import reactDateTimeStyles from 'react-datetime/css/react-datetime.css';

const documentsTranslations = [
  'documents',
  'documenten',
  'dokumente',
  'documentos',
  'documenti',
  'dokumenty',
];

Sentry.init({
  dsn:
    'https://8698d59eaa004008ab5c51a53bc029c9@o78139.ingest.sentry.io/5355853',
  release: 'claim-calculator@20220325.2',
  integrations: [new BrowserTracing()],
  tracesSampleRate: 0.2,
});

const timeZone = 'Africa/Johannesburg';
const dayNames = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const Layout = ({ children }) => {
  const [theme, setThemes] = useState(config?.type || 'greenclaim');
  const { translations: dynamicTranslations, apiUrl } = useCoBrandingParams();
  const now = new Date();
  const start = new Date(new Date().setHours(9, 0, 0));
  const end = new Date(new Date().setHours(12, 30, 0));
  const nowInJohannesburg = new Date(
    now.toLocaleString('en-US', { timeZone: timeZone })
  );
  const startInJohannesburg = new Date(
    start.toLocaleString('en-US', { timeZone: timeZone })
  );
  const endInJohannesburg = new Date(
    end.toLocaleString('en-US', { timeZone: timeZone })
  );
  // eslint-disable-next-line no-unused-vars
  const liveNow = isWithinInterval(nowInJohannesburg, {
    start: startInJohannesburg,
    end: endInJohannesburg,
  });
  const dayInJohannesburg = dayNames[nowInJohannesburg.getDay()];
  // eslint-disable-next-line no-unused-vars
  const isWeekday =
    dayInJohannesburg !== dayNames[0] && dayInJohannesburg !== dayNames[6];

  // Checking if current page is documents so we can run the script for the live-chat
  const isDocumentsPage = documentsTranslations.some(docString =>
    typeof window !== 'undefined'
      ? window.location.href.includes(docString)
      : false
  );

  if (
    typeof window !== 'undefined' &&
    config?.isVisaSite &&
    window?.location?.search.includes('visaCardIssuer')
  ) {
    const urlParams = new URLSearchParams(window.location.search);
    const myParam = urlParams.get('visaCardIssuer');
    localStorage.setItem('yo:visaCardIssuer', myParam);
  }

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    if (!sessionStorage.getItem('uuid')) {
      jsonApiAxios.get('/session').then(({ data }) => {
        sessionStorage.setItem('uuid', data?.uuid);

        window.dataLayer.push({
          event: 'setUserData',
          'user-Id': sessionStorage.getItem('uuid'),
        });
      });
    } else if (sessionStorage.getItem('uuid')) {
      window.dataLayer.push({
        event: 'setUserData',
        'user-Id': sessionStorage.getItem('uuid'),
      });
    }
    // eslint-disable-next-line
  }, []);

  // Setting themes for partners

  useEffect(() => {
    const partner = sessionStorage.getItem('partner')?.split('-')[0];
    if (partner) {
      setThemes(partner);
    }
  }, []);

  useEffect(() => {
    if (theme) {
      applyTheme(theme);
    }
  }, [theme]);

  return (
    <Provider url={apiUrl || '/'} options={useHttpOptions}>
      <IntlProvider
        locale={config.locale || 'en'}
        messages={dynamicTranslations || translations}
      >
        <Helmet>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, user-scalable=no"
          />
          <link href={config?.fontsUrl} rel="stylesheet" type="text/css" />
          <script>
            {dataLayer(config) +
              (process.env.NODE_ENV === 'production' &&
                gtm(config.calculatorDomain))}
          </script>
          {process.env.NODE_ENV === 'production' && (
            <script
              type="text/javascript"
              id="hs-script-loader"
              async
              defer
              src="//js.hs-scripts.com/26198936.js"
            ></script>
          )}
          {isDocumentsPage && (
            <script
              src={config.apiUrl + `/build/chat/dist/widget.prod.js`}
              defer
            ></script>
          )}

          {process.env.NODE_ENV === 'production' && (
              <link
                rel="preconnect"
                href="https://dev.visualwebsiteoptimizer.com"
              />
            ) && (
              <script type="text/javascript" id="vwoCode">
                {`window._vwo_code || (function() {var account_id=680627,version=2.1,settings_tolerance=2000,hide_element='body',hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();`}
              </script>
            )}
        </Helmet>
        {isDocumentsPage && <yource-chat></yource-chat>}
        {children}
        <Global
          styles={css`
            ${reactDateTimeStyles}
            .rdtPicker {
              ${tw`text-md font-open-sans px-15 py-5 rounded`}
            }
            .rdtPicker:not(.rdtStatic) {
              ${tw`shadow-none mt-0 mb-0 border-primary-400`}
            }
            .rdtPicker td {
              ${tw`w-40 h-30 my-5 rounded`}
            }
            .rdtPicker .rdtActive {
              ${tw`bg-primary-400`}
              &:hover {
                ${tw`bg-primary-200`}
              }
            }
            .rdtPicker th,
            .rdtPicker tr {
              ${tw`h-40 font-normal border-none`}
            }
            .rdtPicker .dow {
              ${tw`uppercase text-gray-300 text-sm`}
            }
            .rdtPicker .rdtSwitch {
              ${tw`font-semibold`}
            }
            .rdtPicker .rdtNext,
            .rdtPicker .rdtPrev {
              ${tw`text-primary-400 text-xxl`}
            }
            .rdtPicker .rdtToday::before {
              ${tw`border-primary-400 border-r-0 border-t-0 rounded-full`}
            }
          `}
        />
        {process.env.NODE_ENV === 'production' &&
          config?.site &&
          hotjarIdMap?.[config?.site] && (
            <script
              dangerouslySetInnerHTML={{
                __html: hotjar(config.site),
              }}
            />
          )}
      </IntlProvider>
    </Provider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;
